import React, { useState, useEffect, useCallback } from "react";
import { onedriveAPI } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import {
  Folder,
  FolderOpen,
  FileText,
  File,
  Image,
  ChevronRight,
  ArrowLeft,
  Loader2,
  Cloud,
  Check,
  FileSpreadsheet,
} from "lucide-react";

const getFileIcon = (name, mime) => {
  if (mime?.startsWith("image/")) return Image;
  if (mime === "application/pdf" || name?.endsWith(".pdf")) return FileText;
  if (name?.match(/\.(xlsx?|csv)$/i)) return FileSpreadsheet;
  return File;
};

const formatSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export function OneDriveFilePicker({ open, onClose, onSelect, multiple = true }) {
  const [currentPath, setCurrentPath] = useState("");
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const breadcrumbs = currentPath
    ? ["OneDrive", ...currentPath.split("/")]
    : ["OneDrive"];

  const loadItems = useCallback(async (path) => {
    setLoading(true);
    setError(null);
    try {
      const res = await onedriveAPI.listItems(path);
      setFolders(res.data.folders);
      setFiles(res.data.files);
    } catch (err) {
      setError(err.response?.data?.detail || "Errore nel caricamento");
      setFolders([]);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setCurrentPath("");
      setSelectedFiles([]);
      loadItems("");
    }
  }, [open, loadItems]);

  const navigateTo = (folderName) => {
    const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
    setCurrentPath(newPath);
    loadItems(newPath);
  };

  const goBack = () => {
    const parts = currentPath.split("/");
    parts.pop();
    const newPath = parts.join("/");
    setCurrentPath(newPath);
    loadItems(newPath);
  };

  const goToBreadcrumb = (index) => {
    if (index === 0) {
      setCurrentPath("");
      loadItems("");
    } else {
      const parts = currentPath.split("/");
      const newPath = parts.slice(0, index).join("/");
      setCurrentPath(newPath);
      loadItems(newPath);
    }
  };

  const toggleFile = (file) => {
    const filePath = currentPath ? `${currentPath}/${file.name}` : file.name;
    const fileRef = {
      filename: file.name,
      onedrive_item_id: file.id,
      onedrive_path: filePath,
      size: file.size,
      mime_type: file.mime_type,
    };

    if (!multiple) {
      setSelectedFiles([fileRef]);
      return;
    }

    setSelectedFiles((prev) => {
      const exists = prev.find((f) => f.onedrive_item_id === file.id);
      if (exists) return prev.filter((f) => f.onedrive_item_id !== file.id);
      return [...prev, fileRef];
    });
  };

  const isSelected = (fileId) => selectedFiles.some((f) => f.onedrive_item_id === fileId);

  const handleConfirm = () => {
    onSelect(selectedFiles);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <Cloud size={20} className="text-[#2D7BB7]" />
            Seleziona File da OneDrive
          </DialogTitle>
        </DialogHeader>

        {/* Breadcrumb */}
        <div className="flex items-center gap-1 text-sm flex-wrap px-1" data-testid="file-picker-breadcrumb">
          {breadcrumbs.map((crumb, i) => (
            <React.Fragment key={i}>
              {i > 0 && <ChevronRight size={14} className="text-muted-foreground" />}
              <button
                onClick={() => goToBreadcrumb(i)}
                className={`hover:text-[#2D7BB7] transition-colors px-1 py-0.5 rounded ${
                  i === breadcrumbs.length - 1 ? "text-[#2D7BB7] font-medium" : "text-muted-foreground"
                }`}
              >
                {crumb}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Items list */}
        <div className="flex-1 min-h-[320px] max-h-[450px] overflow-y-auto border border-border rounded-lg">
          {loading ? (
            <div className="flex items-center justify-center h-full py-12">
              <Loader2 className="h-6 w-6 animate-spin text-[#2D7BB7]" />
            </div>
          ) : error ? (
            <div className="p-4 text-center text-red-600 text-sm">{error}</div>
          ) : (
            <div className="divide-y divide-border">
              {currentPath && (
                <button
                  onClick={goBack}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-accent/50 transition-colors text-left"
                  data-testid="file-picker-go-back"
                >
                  <ArrowLeft size={18} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">..</span>
                </button>
              )}
              {/* Folders */}
              {folders.map((folder) => (
                <button
                  key={folder.id}
                  onClick={() => navigateTo(folder.name)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-accent/50 transition-colors text-left group"
                  data-testid={`file-picker-folder-${folder.name}`}
                >
                  <Folder size={18} className="text-amber-500 group-hover:hidden shrink-0" />
                  <FolderOpen size={18} className="text-amber-500 hidden group-hover:block shrink-0" />
                  <span className="flex-1 text-sm font-medium text-foreground">{folder.name}</span>
                  <ChevronRight size={16} className="text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0" />
                </button>
              ))}
              {/* Files */}
              {files.map((file) => {
                const FileIcon = getFileIcon(file.name, file.mime_type);
                const selected = isSelected(file.id);
                return (
                  <button
                    key={file.id}
                    onClick={() => toggleFile(file)}
                    className={`w-full flex items-center gap-3 px-4 py-3 transition-colors text-left ${
                      selected ? "bg-sky-50 border-l-2 border-l-[#2D7BB7]" : "hover:bg-accent/50"
                    }`}
                    data-testid={`file-picker-file-${file.name}`}
                  >
                    <FileIcon size={18} className={`shrink-0 ${selected ? "text-[#2D7BB7]" : "text-gray-500"}`} />
                    <div className="flex-1 min-w-0">
                      <span className={`text-sm block ${selected ? "font-medium text-[#2D7BB7]" : "text-foreground"}`} title={file.name}>
                        {file.name}
                      </span>
                      <span className="text-xs text-muted-foreground">{formatSize(file.size)}</span>
                    </div>
                    {selected && <Check size={18} className="text-[#2D7BB7] shrink-0" />}
                  </button>
                );
              })}
              {folders.length === 0 && files.length === 0 && (
                <div className="p-6 text-center text-muted-foreground text-sm">Cartella vuota</div>
              )}
            </div>
          )}
        </div>

        {/* Selection info */}
        {selectedFiles.length > 0 && (
          <div className="px-3 py-2 bg-sky-50 rounded-lg text-sm text-sky-800 border border-sky-200">
            {selectedFiles.length} file selezionat{selectedFiles.length === 1 ? "o" : "i"}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button
            onClick={handleConfirm}
            disabled={selectedFiles.length === 0}
            className="bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white"
            data-testid="file-picker-confirm-btn"
          >
            <Check size={16} className="mr-2" />
            Conferma ({selectedFiles.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
